// Fake P2P-era data generators

const PEER_HANDLES = [
  "xXxRaVeKiNg2003xXx", "dial_up_dylan", "napsteRnightmare", "MuSiCpHaN_95",
  "dr_souljaboy", "LiMewireLoRd", "d3athm3tal_99", "kazaa_keyboard",
  "cd_burner_2002", "mp3-maniac", "winampwarrior", "floppy_fiona",
  "gigabyte_gary", "SmArtyPaNts", "FsTr_tHan_U", "dsl_demon",
  "aol_angel_88", "yahoo_yoda", "icq_igor", "bluetooth_baby",
  "broadband_bob", "trial.exe", "TRIAL_NOT_VIRUS_TRUST_ME",
  "SoNiC_Y0uTh", "msN_mAnIa", "gEoCiTiEs_GuY", "pHiLe_sHaReR",
];

const TORRENT_TAGS = [
  "safe", "clean", "verified", "ok", "working", "hq", "320", "192",
  "2", "3", "1", "5", "12", "x2", "HQ", "CLEAN", "RAW", "CD_RIP",
  "napster", "kazaa", "lime", "fast", "trusted", "NEW", "REAL",
  "no_virus", "final", "edit", "radio", "full", "128kbps",
];

const TAG_SUFFIXES = [
  "FINAL", "REAL", "NOT_VIRUS", "CLEAN", "HQ", "RADIO_EDIT",
  "ALBUM_VER", "320kbps", "SUPER_CLEAN", "REMASTERED", "LIVE_BONUS",
  "EXTENDED", "FULL_ALBUM_TRACK", "v2", "v3_FINAL_FINAL", "DIRECT_RIP",
  "NOT_A_WORM", "MY_BIRTHDAY_MIX", "BURNED_FROM_CD",
];

const BITRATES = [128, 160, 192, 224, 256, 320];
const EXTENSIONS = ["mp3", "mp3", "mp3", "mp3", "wma", "mp3.part", "mp3"];

// deterministic pseudo-random from a seed string
function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function seeded(seed) {
  let s = seed;
  return () => {
    s = Math.imul(1664525, s) + 1013904223;
    s = s >>> 0;
    return s / 0xFFFFFFFF;
  };
}

function sanitize(str, { lower = false } = {}) {
  const s = (str || "")
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  return lower ? s.toLowerCase() : s;
}

/**
 * Deterministically generate a fun torrent-era MP3 filename.
 * Picks 1 of 5 stylistic variants per track.
 */
export function generateFakeFilename(track) {
  const rand = seeded(hashStr(`${track.id}|${track.title}`));
  const style = Math.floor(rand() * 5);
  const ext = EXTENSIONS[Math.floor(rand() * EXTENSIONS.length)];

  // Variant A: lowercase torrent-style — crazy_frog_(safe)_(3).mp3
  if (style === 0) {
    const artist = sanitize(track.artist, { lower: true }).slice(0, 24);
    const title = sanitize(track.title, { lower: true }).slice(0, 30);
    const tagCount = 1 + Math.floor(rand() * 3);
    const tags = [];
    for (let i = 0; i < tagCount; i++) {
      tags.push(`(${TORRENT_TAGS[Math.floor(rand() * TORRENT_TAGS.length)]})`);
    }
    return `${artist}_${title}_${tags.join("_")}.${ext}`;
  }

  // Variant B: old Napster style — Artist - Title (2).mp3
  if (style === 1) {
    const artist = sanitize(track.artist).slice(0, 28);
    const title = sanitize(track.title).slice(0, 34);
    const num = 1 + Math.floor(rand() * 12);
    return `${artist.replace(/_/g, " ")} - ${title.replace(/_/g, " ")} (${num}).${ext}`;
  }

  // Variant C: SCENE release style — [NAPSTER]-Artist-Title-[CLEAN_2003].mp3
  if (style === 2) {
    const artist = sanitize(track.artist).slice(0, 24);
    const title = sanitize(track.title).slice(0, 30);
    const year = 2000 + Math.floor(rand() * 9);
    const brand = ["NAPSTER", "KAZAA", "LIME", "MORPHEUS", "BEARSHARE", "iMESH"][Math.floor(rand() * 6)];
    const suffix = TAG_SUFFIXES[Math.floor(rand() * TAG_SUFFIXES.length)];
    return `[${brand}]-${artist}-${title}-[${suffix}_${year}].${ext}`;
  }

  // Variant D: verbose BRACKETED — Artist_-_Title_[FINAL_MIX]_320kbps.mp3
  if (style === 3) {
    const artist = sanitize(track.artist).slice(0, 28);
    const title = sanitize(track.title).slice(0, 36);
    const tag = TAG_SUFFIXES[Math.floor(rand() * TAG_SUFFIXES.length)];
    const bitrate = BITRATES[Math.floor(rand() * BITRATES.length)];
    return `${artist}-_${title}_[${tag}]_${bitrate}kbps.${ext}`;
  }

  // Variant E: grainy chatroom/IRC style — track01_-_artist_title.mp3
  {
    const artist = sanitize(track.artist, { lower: true }).slice(0, 24);
    const title = sanitize(track.title, { lower: true }).slice(0, 30);
    const trackNum = String(1 + Math.floor(rand() * 16)).padStart(2, "0");
    return `track${trackNum}_-_${artist}_${title}.${ext}`;
  }
}

export function pickFakeBitrate(track) {
  const rand = seeded(hashStr(track.id + "br"));
  return BITRATES[Math.floor(rand() * BITRATES.length)];
}

export function calcFakeSize(track, bitrate) {
  const br = bitrate || pickFakeBitrate(track);
  const durSec = Math.max(30, Math.round((track.duration_ms || 210000) / 1000));
  const kb = (br * durSec) / 8;
  return kb;
}

export function formatSize(kb) {
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

export function formatDuration(ms) {
  if (!ms || ms <= 0) return "0:30";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatTime(seconds) {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function fakeScanStatus(track) {
  const rand = seeded(hashStr(track.id + "scanstatus"));
  const r = rand();
  if (r < 0.05) return "suspicious"; // ~1–2 per 25
  if (r < 0.22) return "unverified"; // ~3–4 per 25
  return "clean";
}

const THREAT_NAMES = [
  "Trojan.Limewire.Gh0st",
  "Worm.Kazaa.B",
  "Backdoor.Napster.7",
  "Dialer.CrazyFrog.XYZ",
  "Virus.MyDoom.Z",
  "Adware.BonziBuddy",
  "Spyware.Gator.A",
  "Trojan.iMesh.Drop",
  "Worm.BEARSHARE.C",
  "Keylogger.AIM.2003",
  "Backdoor.MSN.Nudge",
  "Virus.ILoveYou.vbs",
  "Adware.WeatherBug",
  "Rootkit.Sony.XCP",
  "Dropper.Winamp.Skin",
];

/**
 * Deterministic list of fake threat names for a track, based on its scan status.
 * - clean       → always 0 threats
 * - unverified  → 60% none, 40% 1–3 threats (leans toward 1)
 * - suspicious  → always 2–5 threats
 */
export function fakeThreats(track, status) {
  const s = status || fakeScanStatus(track);
  if (s === "clean") return [];
  const rand = seeded(hashStr(track.id + "threats"));
  let count;
  if (s === "suspicious") {
    count = 2 + Math.floor(rand() * 4); // 2–5
  } else {
    // unverified — 60% chance of clean, otherwise 1–3
    if (rand() < 0.6) return [];
    count = 1 + Math.floor(rand() * 3); // 1–3
  }
  const used = new Set();
  const out = [];
  while (out.length < count) {
    const name = THREAT_NAMES[Math.floor(rand() * THREAT_NAMES.length)];
    if (used.has(name)) continue;
    used.add(name);
    out.push(name);
  }
  return out;
}

export function fakePeerCount(track) {
  const rand = seeded(hashStr(track.id + "peers"));
  return Math.floor(rand() * 987) + 12;
}

export function fakeSpeed(track) {
  const rand = seeded(hashStr(track.id + "speed"));
  return Math.floor(rand() * 380) + 42;
}

export function fakeSeed(track) {
  const rand = seeded(hashStr(track.id + "seed"));
  return Math.floor(rand() * 42) + 1;
}

export function randomPeer() {
  return PEER_HANDLES[Math.floor(Math.random() * PEER_HANDLES.length)];
}

export const SCAN_STEPS = [
  "Connecting to secure scanner node...",
  "Downloading virus definitions (trial version)...",
  "Checking MD5 signature...",
  "Analyzing audio payload...",
  "Searching for embedded worms...",
  "Scanning for I-LOVE-YOU.vbs...",
  "Verifying bitrate integrity...",
  "Checking for Trojan.Napster.C...",
  "Consulting your uncle who works at Microsoft...",
  "Asking peer network for consensus...",
  "Finalizing certificate of bangerness...",
];
